// src/components/LeftBar.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  type Variants,
  useAnimation,
} from "framer-motion";
import {
  Circle,
  Package,
  Boxes,
  LogOut,
  ShoppingCart,
  AlertTriangle,
  PauseCircle,
  Dot,
  Wrench,
  Building2,
  Ship,
  Users,
  FileText,
  ClipboardList,
  Bug,
  Plus,
  FileSpreadsheet,
  List,
  Zap,
  ChevronDown,
} from "lucide-react";

import MenuIcon from "../assets/icons/menu.svg";

import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/auth-context";

type MenuActionType = "route" | "function";

type ModulePermissions = {
  canView?: boolean | null;
  canCreate?: boolean | null;
  canUpdate?: boolean | null;
  canDelete?: boolean | null;
  canReport?: boolean | null;
};

type SubItem = {
  label: string;
  codigo?: string;
  tipoItem?: MenuActionType;
  actionKey?: string | null;
  routeEnabled?: boolean;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string;
  children?: SubItem[];
};

type MainItem = {
  id?: number | string | null;
  label: string;
  codigo?: string;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string;
  permissions?: ModulePermissions | null;
  subitems: SubItem[];
};

type Department = {
  id?: number | string | null;
  name: string;
  items: MainItem[];
  slug?: string;
  routeKey?: string;
  ordem?: number | null;
  themeColor?: string;
  iconKey?: string;
};

type AddressOption = {
  value: string;
  label: string;
  lines: string[];
  isAll?: boolean;
};

type SessaoEndereco = {
  id?: number | string | null;
  label?: string | null;
  nome?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
};

type SessaoDepartamento = {
  id?: number | string | null;
  nome?: string;
  slug?: string;
  routeKey?: string | null;
  route_key?: string | null;
  ordem?: number | null;
  themeColor?: string | null;
  theme_color?: string | null;
  iconKey?: string | null;
  icon_key?: string | null;
  modulos?: any[];
};

type SessaoEnderecoComModulos = {
  id?: number | string | null;
  departamentos_acessiveis?: SessaoDepartamento[];
};

type FlyoutColumn =
  | { type: "single"; item: MainItem }
  | { type: "standby_group"; items: MainItem[] };

type ModuleActionScope = {
  label: string;
  codigo?: string | null;
  pathLabels: string[];
  pathCodigos: (string | null)[];
};

type ModuleAction = {
  actionKey: string;
  label: string;
  codigo?: string | null;
  source: SubItem;
};

const LEFTBAR_CLASS = "eva-leftbar";
const ALL_ADDRESSES_VALUE = "__ALL__";
const STORAGE_KEY_SELECTED_ADDRESS = "eva:leftbar:selected-address-global";

const HEADER_HEIGHT = 52;
const RAIL_WIDTH = 64;

const FLYOUT_TOP = HEADER_HEIGHT;
const FLYOUT_LEFT = RAIL_WIDTH;

const FLYOUT_PADDING_X = 14;
const FLYOUT_PADDING_Y = 10;
const FLYOUT_BOTTOM_GAP = 18;

const COLUMN_WIDTH = 252;
const COLUMN_MIN_WIDTH = 236;
const COLUMN_GAP = 10;

const MAIN_THAT_GO_TO_FIRST_SUB = new Set(["saidas", "saídas"]);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    marginTop: `${HEADER_HEIGHT}px`,
    position: "fixed",
    top: 0,
    left: 0,
    width: `${RAIL_WIDTH}px`,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    zIndex: 1200,
    boxSizing: "border-box",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    transition: "transform 0.3s ease-in-out",
    overflow: "visible",
    userSelect: "none",
  },

  scrollArea: {
    height: "100%",
    width: "100%",
    overflowY: "auto",
    overflowX: "visible",
    boxSizing: "border-box",
  },

  inner: {
    padding: "0 0 10px",
    minHeight: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 0,
  },

  overlay: {
    position: "fixed",
    top: HEADER_HEIGHT,
    left: RAIL_WIDTH,
    right: 0,
    height: 0,
    backgroundColor: "transparent",
    zIndex: 1150,
    pointerEvents: "none",
  },

  mobileHeader: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: `${HEADER_HEIGHT}px`,
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    justifyContent: "space-between",
    color: "#fff",
    zIndex: 1300,
  },

  titleMobile: {
    flex: 1,
    fontSize: "14px",
    fontWeight: 500,
    textAlign: "center",
    fontFamily: "'Poppins', sans-serif",
    paddingRight: "26px",
  },

  migrationMarksWrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    marginLeft: "auto",
  },

  migrationMark: {
    width: 3,
    height: 15,
    borderRadius: 999,
    background: "rgba(16,185,129,0.95)",
    boxShadow: "0 0 0 1px rgba(6,95,70,0.35)",
  },

  migrationMarkOrange: {
    width: 3,
    height: 15,
    borderRadius: 999,
    background: "rgba(249,115,22,0.95)",
    boxShadow: "0 0 0 1px rgba(154,52,18,0.35)",
  },

  migrationMarkBlue: {
    width: 3,
    height: 15,
    borderRadius: 999,
    background: "rgba(59,130,246,0.95)",
    boxShadow: "0 0 0 1px rgba(30,64,175,0.35)",
  },
};

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

const getDeptPermKey = (d: Department) =>
  d.slug ? String(d.slug) : slugify(d.name);

const getDeptRouteKey = (d: Department) =>
  d.routeKey ? String(d.routeKey) : d.slug ? String(d.slug) : slugify(d.name);

const getMainSlug = (item: MainItem) => slugify(item.label);

const cleanCodigoForRoute = (dept: Department, codigo?: string) => {
  const raw = String(codigo || "").trim();
  if (!raw) return "";

  const deptKey = slugify(getDeptRouteKey(dept));
  const parts = slugify(raw).split("-").filter(Boolean);

  if (!parts.length) return "";
  if (parts[0] === deptKey) parts.shift();
  if (parts[parts.length - 1] === deptKey) parts.pop();

  return parts.join("-");
};

const getSubSlug = (dept: Department, sub: SubItem) => {
  const cleaned = cleanCodigoForRoute(dept, sub.codigo);
  return cleaned ? cleaned : slugify(sub.label);
};

const isFunctionItem = (sub: SubItem) =>
  sub.tipoItem === "function" || sub.routeEnabled === false;

const getActionKey = (sub: SubItem): string | null => {
  if (sub.actionKey && String(sub.actionKey).trim()) {
    return String(sub.actionKey).trim();
  }

  const normalized = norm(sub.label);

  if (normalized.includes("gerar") && normalized.includes("relatorio")) {
    return "generate_report";
  }

  return null;
};

const isReportAction = (sub: SubItem) => {
  const key = getActionKey(sub);
  return key === "generate_report";
};

const getScopeFromPath = (path: SubItem[]): ModuleActionScope | null => {
  const routePath = path.filter((entry) => !isFunctionItem(entry));

  if (!routePath.length) {
    return {
      label: "Geral do módulo",
      codigo: null,
      pathLabels: [],
      pathCodigos: [],
    };
  }

  const last = routePath[routePath.length - 1];

  return {
    label: routePath.map((x) => x.label).join(" / "),
    codigo: last.codigo || null,
    pathLabels: routePath.map((x) => x.label),
    pathCodigos: routePath.map((x) => x.codigo || null),
  };
};

const collectModuleActions = (subitems: SubItem[] = []): ModuleAction[] => {
  const actionsMap = new Map<string, ModuleAction>();

  const walk = (nodes: SubItem[]) => {
    for (const node of nodes || []) {
      const actionKey = getActionKey(node);

      if (isFunctionItem(node) && actionKey) {
        if (!actionsMap.has(actionKey)) {
          actionsMap.set(actionKey, {
            actionKey,
            label:
              actionKey === "generate_report"
                ? "Gerar Relatório"
                : node.label || "Executar Ação",
            codigo: node.codigo || null,
            source: node,
          });
        }
      }

      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children);
      }
    }
  };

  walk(subitems);

  return Array.from(actionsMap.values()).sort((a, b) => {
    if (a.actionKey === "generate_report") return -1;
    if (b.actionKey === "generate_report") return 1;
    return a.label.localeCompare(b.label, "pt-BR");
  });
};

const getRenderableSubitems = (subitems: SubItem[] = []): SubItem[] => {
  const walk = (nodes: SubItem[]): SubItem[] => {
    const output: SubItem[] = [];

    for (const node of nodes || []) {
      const children = Array.isArray(node.children) ? walk(node.children) : [];

      if (isReportAction(node)) {
        output.push(...children);
        continue;
      }

      if (isFunctionItem(node)) {
        output.push(...children);
        continue;
      }

      output.push({
        ...node,
        children,
      });
    }

    return output;
  };

  return walk(subitems);
};

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [13, 60, 70];

  const clean = hex.replace("#", "");
  if (clean.length !== 6) return [13, 60, 70];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [13, 60, 70];
  }

  return [r, g, b];
}

const toRgba = (hex: string | undefined, alpha = 1) => {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const darkenHex = (hex: string | undefined, amount = 16) => {
  const [r, g, b] = hexToRgbComponents(hex);

  const nr = clamp(r - amount, 0, 255);
  const ng = clamp(g - amount, 0, 255);
  const nb = clamp(b - amount, 0, 255);

  return `rgb(${nr}, ${ng}, ${nb})`;
};

const DevBadge: React.FC = () => (
  <span
    title="Ainda não finalizado"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: 5,
      borderRadius: 6,
      padding: "1px 4px",
      background: "rgba(255, 200, 0, 0.20)",
      border: "1px solid rgba(255, 200, 0, 0.52)",
      flexShrink: 0,
    }}
  >
    <Wrench size={11} style={{ color: "#FFD34D" }} />
  </span>
);

const DeptInternetIcon: React.FC<{
  iconKey?: string;
  size?: number;
  color?: string;
  alt?: string;
  style?: React.CSSProperties;
}> = ({ iconKey, size = 19, color = "#ffffff", alt = "", style }) => {
  const safeKey =
    typeof iconKey === "string" && iconKey.includes(":")
      ? iconKey
      : "lucide:circle";

  const src = `https://api.iconify.design/${safeKey}.svg?color=${encodeURIComponent(
    color
  )}`;

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      draggable={false}
      loading="eager"
      style={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
        userSelect: "none",
        pointerEvents: "none",
        ...style,
      }}
      onError={(e) => {
        e.currentTarget.src =
          "https://api.iconify.design/lucide:circle.svg?color=%23ffffff";
      }}
    />
  );
};

const OptionIcon: React.FC<{
  label: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ label, size = 17, className, style }) => {
  const rawLower = label.trim().toLowerCase();
  const base = norm(label).replace(/\s+/g, "-").split("-")[0];
  const common = { className, style };

  if (rawLower === "colaboradores") return <Users size={size} {...common} />;

  if (base === "circle-outline") {
    return <Circle size={13} {...common} style={{ ...style, opacity: 0.9 }} />;
  }

  if (
    rawLower === "d. empresa" ||
    rawLower === "documentacao da empresa" ||
    rawLower === "documentação da empresa"
  ) {
    return <FileText size={size} {...common} />;
  }

  if (
    rawLower === "solicitações" ||
    rawLower === "solicitacoes" ||
    rawLower === "minhas solicitações" ||
    rawLower === "minhas solicitacoes"
  ) {
    return <ClipboardList size={size} {...common} />;
  }

  if (rawLower === "problemas") return <Bug size={size} {...common} />;
  if (base === "produtos") return <Package size={size} {...common} />;
  if (base === "estoque") return <Boxes size={size} {...common} />;
  if (base === "saidas" || base === "saídas")
    return <LogOut size={size} {...common} />;
  if (base === "vendas") return <ShoppingCart size={size} {...common} />;
  if (base === "perdas") return <AlertTriangle size={size} {...common} />;
  if (base === "standby") return <PauseCircle size={size} {...common} />;
  if (base === "empresas") return <Building2 size={size} {...common} />;
  if (base === "embarcacoes") return <Ship size={size} {...common} />;
  if (base === "circle") return <Dot size={14} {...common} />;

  return (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        ...style,
      }}
    />
  );
};

const MiniActionIcon: React.FC<{ label: string }> = ({ label }) => {
  const n = norm(label);

  if (n.includes("movimentacao") || n.includes("movimentação"))
    return <Zap size={10} />;

  if (n.includes("incluir") || n.includes("registrar"))
    return <Plus size={10} />;

  if (n.includes("planilha")) return <FileSpreadsheet size={10} />;

  if (n.includes("exibir") || n.includes("listar")) return <List size={10} />;

  if (n.includes("faturar") || n.includes("etapas")) return <Zap size={10} />;

  return <Dot size={10} />;
};

const sidebarVariants: Variants = {
  hidden: { x: "-100%", opacity: 0 },
  show: {
    x: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 260, damping: 26 },
  },
};

const flyoutVariants: Variants = {
  hidden: { opacity: 0, y: -4 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.14 },
  },
  exit: {
    opacity: 0,
    y: -2,
    transition: { duration: 0.12 },
  },
};

const mobileDeptVariants: Variants = {
  collapsed: { height: 0, opacity: 0, transition: { duration: 0.18 } },
  open: {
    height: "auto",
    opacity: 1,
    transition: { type: "spring", stiffness: 250, damping: 26 },
  },
};

const contentSwapVariants: Variants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.18 } },
  exit: { opacity: 0, y: -4, transition: { duration: 0.12 } },
};

const LeftBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openDepts, setOpenDepts] = useState<string[]>([]);
  const [collapsedByUser, setCollapsedByUser] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [hoveredDeptName, setHoveredDeptName] = useState<string | null>(null);
  const [hoveredRailDeptName, setHoveredRailDeptName] = useState<string | null>(
    null
  );

  const [selectedAddressValue, setSelectedAddressValue] = useState<string>("");

  const [openAddressDropdownByDept, setOpenAddressDropdownByDept] = useState<
    Record<string, boolean>
  >({});

  const [openActionDropdownKey, setOpenActionDropdownKey] = useState<
    string | null
  >(null);

  const closeFlyoutTimer = useRef<number | null>(null);

  const { theme, systemTitle, setDepartmentColor } = useTheme();
  const { sessao } = useAuth();

  useReducedMotion();

  const sidebarControls = useAnimation();

  const roles = sessao?.roles || [];
  const hasRoleSolicitante = roles.includes("solicitante");

  const userDept = (sessao?.departamento || "").toLowerCase();
  const isDev = userDept === "desenvolvedor";

  const primaryColor = theme?.primary || "#0D3C46";
  const menuButtonsSelection = theme?.menu_buttons_selection || "#19D4CF";

  const flyoutBg = "#FFFFFF";
  const flyoutBorder = "rgba(15, 23, 42, 0.08)";

  const iconIdle = "rgba(255,255,255,0.92)";
  const iconMuted = "rgba(255,255,255,0.60)";
  const iconActive = "#ffffff";

  const mapNullableBoolean = (value: any): boolean | null => {
    if (value === undefined || value === null) return null;
    return value === true;
  };

  const mapModulePermissions = (mod: any): ModulePermissions | null => {
    const source =
      mod?.permissions && typeof mod.permissions === "object"
        ? mod.permissions
        : mod?.permissoes && typeof mod.permissoes === "object"
        ? mod.permissoes
        : null;

    if (!source) return null;

    return {
      canView: mapNullableBoolean(source.canView ?? source.can_view),
      canCreate: mapNullableBoolean(source.canCreate ?? source.can_create),
      canUpdate: mapNullableBoolean(source.canUpdate ?? source.can_update),
      canDelete: mapNullableBoolean(source.canDelete ?? source.can_delete),
      canReport: mapNullableBoolean(source.canReport ?? source.can_report),
    };
  };

  const solicitanteDept: Department = useMemo(
    () => ({
      name: "Minhas Solicitações",
      slug: "minhas-solicitacoes",
      routeKey: "minhas-solicitacoes",
      ordem: -100,
      themeColor: "#0a343d",
      iconKey: "lucide:clipboard-list",
      items: [
        {
          label: "Laboratório - Cases",
          devOnly: false,
          developed: true,
          migrated: false,
          themeColor: "#0a343d",
          subitems: [],
        },
      ],
    }),
    []
  );

  const mapSubItemRecursive = (sub: any, fallbackColor?: string): SubItem => ({
    label: String(sub?.label || ""),
    codigo: String(sub?.codigo || ""),

    tipoItem:
      sub?.tipoItem === "function" || sub?.tipo_item === "function"
        ? "function"
        : "route",

    actionKey:
      sub?.actionKey !== undefined && sub?.actionKey !== null
        ? String(sub.actionKey)
        : sub?.action_key !== undefined && sub?.action_key !== null
        ? String(sub.action_key)
        : null,

    routeEnabled:
      sub?.routeEnabled !== undefined
        ? sub.routeEnabled !== false
        : sub?.route_enabled !== undefined
        ? sub.route_enabled !== false
        : true,

    devOnly: sub?.devOnly === true || sub?.dev_only === true,
    developed: sub?.developed === true,
    migrated: sub?.migrated === true,
    markOrange: sub?.markOrange === true || sub?.mark_orange === true,
    markBlue: sub?.markBlue === true || sub?.mark_blue === true,
    themeColor:
      sub?.themeColor !== undefined && sub?.themeColor !== null
        ? String(sub.themeColor)
        : fallbackColor ?? "#0a343d",
    children: Array.isArray(sub?.children)
      ? sub.children.map((child: any) =>
          mapSubItemRecursive(child, fallbackColor)
        )
      : [],
  });

  const mapDepartmentFromApi = (dept: any, index: number): Department => ({
    id: dept?.id !== undefined && dept?.id !== null ? dept.id : null,
    name: String(dept?.nome || ""),
    slug: String(dept?.slug || ""),
    routeKey:
      dept?.routeKey !== undefined && dept?.routeKey !== null
        ? String(dept.routeKey)
        : dept?.route_key !== undefined && dept?.route_key !== null
        ? String(dept.route_key)
        : String(dept?.slug || ""),
    ordem:
      dept?.ordem !== undefined && dept?.ordem !== null
        ? Number(dept.ordem)
        : index,
    themeColor:
      dept?.themeColor !== undefined && dept?.themeColor !== null
        ? String(dept.themeColor)
        : dept?.theme_color !== undefined && dept?.theme_color !== null
        ? String(dept.theme_color)
        : "#0a343d",
    iconKey:
      dept?.iconKey !== undefined && dept?.iconKey !== null
        ? String(dept.iconKey)
        : dept?.icon_key !== undefined && dept?.icon_key !== null
        ? String(dept.icon_key)
        : "lucide:circle",
    items: Array.isArray(dept?.modulos)
      ? dept.modulos.map((mod: any) => ({
          id: mod?.id !== undefined && mod?.id !== null ? mod.id : null,
          label: String(mod?.label || ""),
          codigo: String(mod?.codigo || ""),
          devOnly: mod?.devOnly === true || mod?.dev_only === true,
          developed: mod?.developed === true,
          migrated: mod?.migrated === true,
          markOrange: mod?.markOrange === true || mod?.mark_orange === true,
          markBlue: mod?.markBlue === true || mod?.mark_blue === true,
          permissions: mapModulePermissions(mod),
          themeColor:
            mod?.themeColor !== undefined && mod?.themeColor !== null
              ? String(mod.themeColor)
              : dept?.themeColor ?? dept?.theme_color ?? "#0a343d",
          subitems: Array.isArray(mod?.subitems)
            ? mod.subitems.map((sub: any) =>
                mapSubItemRecursive(
                  sub,
                  mod?.themeColor ??
                    mod?.theme_color ??
                    dept?.themeColor ??
                    dept?.theme_color ??
                    "#0a343d"
                )
              )
            : [],
        }))
      : [],
  });

  const sessaoEnderecos = useMemo<SessaoEndereco[]>(() => {
    return Array.isArray(sessao?.enderecos_disponiveis)
      ? (sessao?.enderecos_disponiveis as SessaoEndereco[])
      : [];
  }, [sessao?.enderecos_disponiveis]);

  const addressOptions = useMemo<AddressOption[]>(() => {
    const mapped = sessaoEnderecos.map((end) => {
      const nome =
        typeof end?.nome === "string" && end.nome.trim() ? end.nome.trim() : "";

      const linhaPrincipal = [end?.logradouro, end?.numero]
        .filter((x) => typeof x === "string" && String(x).trim().length > 0)
        .map((x) => String(x).trim())
        .join(", ");

      const linhaSecundaria = [end?.bairro, end?.cidade, end?.uf]
        .filter((x) => typeof x === "string" && String(x).trim().length > 0)
        .map((x) => String(x).trim())
        .join(" - ");

      const fallbackLabel =
        typeof end?.label === "string" && end.label.trim()
          ? end.label.trim()
          : [nome, linhaPrincipal, linhaSecundaria]
              .filter(Boolean)
              .join(" • ") || `Endereço ${String(end?.id ?? "")}`;

      const lines = [nome, linhaPrincipal, linhaSecundaria].filter(
        (x) => typeof x === "string" && x.trim().length > 0
      );

      return {
        value: String(end?.id ?? ""),
        label: fallbackLabel,
        lines: lines.length > 0 ? lines : [fallbackLabel],
      } as AddressOption;
    });

    return [
      {
        value: ALL_ADDRESSES_VALUE,
        label: "Todos os endereços",
        lines: ["Todos os endereços"],
        isAll: true,
      },
      ...mapped,
    ];
  }, [sessaoEnderecos]);

  const defaultAddressValue = useMemo(() => {
    if (
      sessao?.endereco_selecionado_id !== undefined &&
      sessao?.endereco_selecionado_id !== null
    ) {
      return String(sessao.endereco_selecionado_id);
    }

    const firstRealAddress = addressOptions.find((opt) => !opt.isAll);
    return firstRealAddress?.value || ALL_ADDRESSES_VALUE;
  }, [sessao?.endereco_selecionado_id, addressOptions]);

  const baseDepartments: Department[] = useMemo(() => {
    const rawDepartments = Array.isArray(sessao?.departamentos_acessiveis)
      ? sessao?.departamentos_acessiveis ?? []
      : [];

    return rawDepartments.map((dept: any, index: number) =>
      mapDepartmentFromApi(dept, index)
    );
  }, [sessao?.departamentos_acessiveis]);

  const departmentsByAddress = useMemo(() => {
    const map = new Map<string, Department[]>();

    const rows: SessaoEnderecoComModulos[] = Array.isArray(
      sessao?.enderecos_com_modulos
    )
      ? (sessao?.enderecos_com_modulos as SessaoEnderecoComModulos[])
      : [];

    rows.forEach((row) => {
      const key = String(row?.id ?? "");

      const deps = Array.isArray(row?.departamentos_acessiveis)
        ? row.departamentos_acessiveis.map((dept: any, index: number) =>
            mapDepartmentFromApi(dept, index)
          )
        : [];

      map.set(key, deps);
    });

    return map;
  }, [sessao?.enderecos_com_modulos]);

  const mergeDepartments = (departmentLists: Department[][]): Department[] => {
    const depMap = new Map<string, Department>();

    for (const list of departmentLists) {
      for (const dept of list || []) {
        const depKey = norm(getDeptRouteKey(dept) || getDeptPermKey(dept));
        const existing = depMap.get(depKey);

        if (!existing) {
          depMap.set(depKey, {
            ...dept,
            items: Array.isArray(dept.items) ? [...dept.items] : [],
          });
          continue;
        }

        const itemMap = new Map<string, MainItem>();

        for (const item of existing.items || []) {
          const key = String(item.codigo || getMainSlug(item));
          itemMap.set(key, item);
        }

        for (const item of dept.items || []) {
          const key = String(item.codigo || getMainSlug(item));
          if (!itemMap.has(key)) {
            itemMap.set(key, item);
          }
        }

        depMap.set(depKey, {
          ...existing,
          themeColor: existing.themeColor || dept.themeColor,
          iconKey: existing.iconKey || dept.iconKey,
          ordem:
            existing.ordem !== undefined && existing.ordem !== null
              ? existing.ordem
              : dept.ordem,
          items: Array.from(itemMap.values()),
        });
      }
    }

    return Array.from(depMap.values()).sort((a, b) => {
      if (Number(a.ordem ?? 9999) !== Number(b.ordem ?? 9999)) {
        return Number(a.ordem ?? 9999) - Number(b.ordem ?? 9999);
      }

      return String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
    });
  };

  const departmentsFromAllAddresses = useMemo(() => {
    const lists = Array.from(departmentsByAddress.values()).filter(
      (deps) => Array.isArray(deps) && deps.length > 0
    );

    if (!lists.length) return baseDepartments;

    return mergeDepartments(lists);
  }, [departmentsByAddress, baseDepartments]);

  const departmentsSource: Department[] = useMemo(() => {
    const selected =
      selectedAddressValue || defaultAddressValue || ALL_ADDRESSES_VALUE;

    const source =
      selected === ALL_ADDRESSES_VALUE
        ? departmentsFromAllAddresses
        : departmentsByAddress.get(String(selected)) ||
          departmentsFromAllAddresses;

    if (!hasRoleSolicitante) return source;

    const already = source.some(
      (d) => norm(getDeptRouteKey(d)) === norm(getDeptRouteKey(solicitanteDept))
    );

    if (already) return source;

    return [solicitanteDept, ...source];
  }, [
    hasRoleSolicitante,
    departmentsFromAllAddresses,
    departmentsByAddress,
    selectedAddressValue,
    defaultAddressValue,
    solicitanteDept,
  ]);

  const canSeeDept = useMemo(() => {
    const accessibleKeys = new Set(
      departmentsSource.map((dept) => norm(getDeptPermKey(dept)))
    );

    return (dept: Department) => {
      const permKey = norm(getDeptPermKey(dept));
      const routeKey = norm(getDeptRouteKey(dept));

      if (hasRoleSolicitante && permKey === "minhas-solicitacoes") return true;

      return accessibleKeys.has(permKey) || accessibleKeys.has(routeKey);
    };
  }, [departmentsSource, hasRoleSolicitante]);

  const itemVisible = (item: MainItem) => (isDev ? true : !item.devOnly);
  const subItemVisible = (sub: SubItem) => (isDev ? true : !sub.devOnly);

  const getDepartmentAddressKey = (dept: Department) =>
    `${getDeptRouteKey(dept)}::address-dropdown`;

  const getSelectedAddressValue = () => {
    return selectedAddressValue || defaultAddressValue || ALL_ADDRESSES_VALUE;
  };

  const getIsAddressDropdownOpen = (dept: Department) =>
    !!openAddressDropdownByDept[getDepartmentAddressKey(dept)];

  const setAddressDropdownOpen = (dept: Department, open: boolean) => {
    const deptKey = getDepartmentAddressKey(dept);

    setOpenAddressDropdownByDept((prev) => ({
      ...prev,
      [deptKey]: open,
    }));
  };

  const getSelectedAddressOption = (): AddressOption | null => {
    const value = getSelectedAddressValue();
    return addressOptions.find((opt) => opt.value === value) || null;
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SELECTED_ADDRESS);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && parsed.trim()) {
        setSelectedAddressValue(parsed);
      }
    } catch {
      // storage inválido: ignora
    }
  }, []);

  useEffect(() => {
    try {
      if (!selectedAddressValue) return;

      window.localStorage.setItem(
        STORAGE_KEY_SELECTED_ADDRESS,
        JSON.stringify(selectedAddressValue)
      );
    } catch {
      // erro de storage: ignora
    }
  }, [selectedAddressValue]);

  useEffect(() => {
    if (!selectedAddressValue) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    const selectedExists = addressOptions.some(
      (opt) => opt.value === selectedAddressValue
    );

    if (!selectedExists && addressOptions.length > 0) {
      setSelectedAddressValue(defaultAddressValue || ALL_ADDRESSES_VALUE);
    }
  }, [addressOptions, defaultAddressValue, selectedAddressValue]);

  useEffect(() => {
    const handleGlobalClick = () => {
      setOpenAddressDropdownByDept({});
      setOpenActionDropdownKey(null);
    };

    window.addEventListener("click", handleGlobalClick);

    return () => window.removeEventListener("click", handleGlobalClick);
  }, []);

  const getDepartmentFromSelectedAddress = (dept: Department): Department => {
    const selectedAddress = getSelectedAddressValue();

    if (norm(getDeptRouteKey(dept)) === "minhas-solicitacoes") {
      return dept;
    }

    if (!selectedAddress) {
      return {
        ...dept,
        items: [],
      };
    }

    if (selectedAddress === ALL_ADDRESSES_VALUE) {
      const allMatch =
        departmentsFromAllAddresses.find(
          (d) =>
            norm(getDeptRouteKey(d)) === norm(getDeptRouteKey(dept)) ||
            norm(getDeptPermKey(d)) === norm(getDeptPermKey(dept))
        ) || dept;

      return {
        ...dept,
        items: Array.isArray(allMatch.items) ? allMatch.items : [],
        themeColor: allMatch.themeColor || dept.themeColor,
        iconKey: allMatch.iconKey || dept.iconKey,
      };
    }

    const addressDepartments =
      departmentsByAddress.get(String(selectedAddress)) || [];

    const matched =
      addressDepartments.find(
        (d) =>
          norm(getDeptRouteKey(d)) === norm(getDeptRouteKey(dept)) ||
          norm(getDeptPermKey(d)) === norm(getDeptPermKey(dept))
      ) || null;

    if (!matched) {
      return {
        ...dept,
        items: [],
      };
    }

    return {
      ...dept,
      items: Array.isArray(matched.items) ? matched.items : [],
      themeColor: matched.themeColor || dept.themeColor,
      iconKey: matched.iconKey || dept.iconKey,
    };
  };

  const handleAddressChange = (_dept: Department, value: string) => {
    setSelectedAddressValue(value);
  };

  const renderAddressSelector = (dept: Department) => {
    const selected = getSelectedAddressOption();
    const isOpenDropdown = getIsAddressDropdownOpen(dept);

    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          minWidth: 0,
          width: 244,
          maxWidth: 244,
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setAddressDropdownOpen(dept, !isOpenDropdown);
            setOpenActionDropdownKey(null);
          }}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            textAlign: "right",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            gap: 6,
          }}
          aria-label={`Selecionar endereço de ${dept.name}`}
        >
          <div
            style={{
              minWidth: 0,
              maxWidth: 218,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              lineHeight: 1.1,
            }}
          >
            {selected ? (
              selected.lines.map((line, index) => (
                <span
                  key={`${selected.value}-${index}`}
                  style={{
                    display: "block",
                    width: "100%",
                    color: index === 0 ? "#505864" : "#7D8694",
                    fontSize: index === 0 ? 11.8 : 10.8,
                    fontWeight: index === 0 ? 600 : 500,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {line}
                </span>
              ))
            ) : (
              <span
                style={{
                  color: "#7D8694",
                  fontSize: 11.5,
                  fontWeight: 500,
                }}
              >
                Sem endereços
              </span>
            )}
          </div>

          <ChevronDown
            size={12}
            strokeWidth={2}
            style={{
              marginTop: 1,
              flexShrink: 0,
              color: "#9AA1AC",
              transform: isOpenDropdown ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 160ms ease",
            }}
          />
        </button>

        <AnimatePresence>
          {isOpenDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.14 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                width: 270,
                maxHeight: 236,
                overflowY: "auto",
                background: "#FFFFFF",
                border: "1px solid rgba(15,23,42,0.08)",
                borderRadius: 12,
                boxShadow: "0 16px 32px rgba(15,23,42,0.12)",
                padding: 6,
                zIndex: 3000,
              }}
            >
              {addressOptions.length === 0 ? (
                <div
                  style={{
                    padding: "8px 10px",
                    color: "#FFFFFF",
                    fontSize: 12,
                  }}
                >
                  Sem endereços
                </div>
              ) : (
                addressOptions.map((option) => {
                  const selectedCurrent = selected?.value === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        handleAddressChange(dept, option.value);
                        setAddressDropdownOpen(dept, false);
                      }}
                      style={{
                        width: "100%",
                        border: "none",
                        background: selectedCurrent
                          ? "rgba(18,66,128,0.08)"
                          : "transparent",
                        borderRadius: 10,
                        padding: "8px 10px",
                        margin: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      {option.lines.map((line, index) => (
                        <span
                          key={`${option.value}-${index}`}
                          style={{
                            display: "block",
                            width: "100%",
                            color: index === 0 ? "#49515C" : "#7D8694",
                            fontSize: index === 0 ? 12 : 11,
                            fontWeight: index === 0 ? 600 : 500,
                            lineHeight: 1.18,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {line}
                        </span>
                      ))}
                    </button>
                  );
                })
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderLabelWithBadge = (
    label: string,
    developed?: boolean,
    migrated?: boolean,
    markOrange?: boolean,
    markBlue?: boolean
  ) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        width: "100%",
        minWidth: 0,
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {label}
      </span>

      {isDev && developed === false ? <DevBadge /> : null}

      {isDev && (migrated || markOrange || markBlue) && (
        <span style={styles.migrationMarksWrapper}>
          {migrated && <span style={styles.migrationMark} />}
          {markOrange && <span style={styles.migrationMarkOrange} />}
          {markBlue && <span style={styles.migrationMarkBlue} />}
        </span>
      )}
    </span>
  );

  const buildSubPath = (
    dept: Department,
    item: MainItem,
    sub: SubItem,
    ancestry: SubItem[] = []
  ): string => {
    const baseDeptPath = `/${getDeptRouteKey(dept)}`;
    const baseMainPath = `${baseDeptPath}/${getMainSlug(item)}`;

    const chain = [...ancestry, sub]
      .map((entry) => getSubSlug(dept, entry))
      .filter(Boolean);

    return `${baseMainPath}/${chain.join("/")}`;
  };

  const getFirstVisibleNestedPath = (
    dept: Department,
    item: MainItem,
    subitems: SubItem[],
    ancestry: SubItem[] = []
  ): string | null => {
    const visibleTree = getRenderableSubitems(subitems).filter(subItemVisible);

    for (const sub of visibleTree) {
      const children = getRenderableSubitems(sub.children || []).filter(
        subItemVisible
      );

      const currentPath = buildSubPath(dept, item, sub, ancestry);

      if (children.length > 0) {
        const deeper = getFirstVisibleNestedPath(dept, item, children, [
          ...ancestry,
          sub,
        ]);

        return deeper || currentPath;
      }

      return currentPath;
    }

    return null;
  };

  const buildFirstPath = (dept: Department) => {
    const deptForAddress = getDepartmentFromSelectedAddress(dept);
    const baseDept = `/${getDeptRouteKey(deptForAddress)}`;

    const visibleItems = deptForAddress.items.filter(itemVisible);

    if (!visibleItems.length) return baseDept;

    const firstItem = visibleItems[0];
    const mainSlug = getMainSlug(firstItem);
    const baseMain = `${baseDept}/${mainSlug}`;

    const visibleSubs = getRenderableSubitems(firstItem.subitems || []).filter(
      subItemVisible
    );

    const isGoFirstSub =
      MAIN_THAT_GO_TO_FIRST_SUB.has(norm(firstItem.label)) &&
      visibleSubs.length > 0;

    if (isGoFirstSub) {
      return (
        getFirstVisibleNestedPath(deptForAddress, firstItem, visibleSubs) ||
        baseMain
      );
    }

    return baseMain;
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1000;

      setIsMobile(mobile);
      setIsOpen(!mobile);
      sidebarControls.start(mobile ? "hidden" : "show");

      if (mobile) {
        setHoveredDeptName(null);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarControls]);

  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return;

    const deptFromPath = parts[0];

    const dept =
      departmentsSource.find(
        (d) => norm(getDeptRouteKey(d)) === norm(deptFromPath)
      ) ||
      departmentsSource.find(
        (d) => norm(getDeptPermKey(d)) === norm(deptFromPath)
      );

    if (!dept) return;
    if (!canSeeDept(dept)) return;

    if (
      !openDepts.includes(dept.name) &&
      !collapsedByUser.includes(dept.name)
    ) {
      setOpenDepts((prev) => [...prev, dept.name]);
    }
  }, [
    location.pathname,
    departmentsSource,
    canSeeDept,
    openDepts,
    collapsedByUser,
  ]);

  useEffect(() => {
    if (!sessao) return;
    if (!departmentsSource.length) return;
    if (isDev) return;

    const parts = location.pathname.split("/").filter(Boolean);

    if (parts.length > 0) {
      const currentDeptFromPath = parts[0];

      const existingDept = departmentsSource.find(
        (d) =>
          norm(getDeptRouteKey(d)) === norm(currentDeptFromPath) ||
          norm(getDeptPermKey(d)) === norm(currentDeptFromPath)
      );

      if (existingDept) {
        setOpenDepts((prev) =>
          prev.includes(existingDept.name) ? prev : [existingDept.name]
        );
        return;
      }
    }

    if (hasRoleSolicitante) {
      const solicitante = departmentsSource.find(
        (d) => norm(getDeptRouteKey(d)) === "minhas-solicitacoes"
      );

      if (solicitante) {
        setOpenDepts((prev) =>
          prev.includes(solicitante.name) ? prev : [solicitante.name]
        );

        navigate(buildFirstPath(solicitante), { replace: true });
        return;
      }
    }

    const preferredDept =
      departmentsSource.find(
        (d) =>
          norm(getDeptPermKey(d)) === norm(userDept) ||
          norm(getDeptRouteKey(d)) === norm(userDept)
      ) || departmentsSource[0];

    if (!preferredDept) return;

    setOpenDepts((prev) =>
      prev.includes(preferredDept.name) ? prev : [preferredDept.name]
    );

    navigate(buildFirstPath(preferredDept), { replace: true });
  }, [
    sessao,
    departmentsSource,
    isDev,
    hasRoleSolicitante,
    userDept,
    location.pathname,
    navigate,
    selectedAddressValue,
  ]);

  const toggleDepartment = (dept: Department) => {
    if (!canSeeDept(dept)) return;

    setOpenDepts((prev) => {
      const isOpenDept = prev.includes(dept.name);

      if (isOpenDept) {
        setCollapsedByUser((c) =>
          c.includes(dept.name) ? c : [...c, dept.name]
        );

        return prev.filter((d) => d !== dept.name);
      }

      setCollapsedByUser((c) => c.filter((d) => d !== dept.name));

      return [...prev, dept.name];
    });
  };

  const parts = location.pathname.split("/").filter(Boolean);
  const currentDeptFromPath = parts.length > 0 ? parts[0] : null;

  const visibleDepartments = useMemo(() => {
    return departmentsSource
      .filter((d) => canSeeDept(d))
      .sort((a, b) => Number(a.ordem ?? 9999) - Number(b.ordem ?? 9999));
  }, [departmentsSource, canSeeDept]);

  const isDeptSelected = (dept: Department) => {
    if (!currentDeptFromPath) return false;

    return (
      norm(currentDeptFromPath) === norm(getDeptRouteKey(dept)) ||
      norm(currentDeptFromPath) === norm(getDeptPermKey(dept))
    );
  };

  const hoveredDept = useMemo(() => {
    if (!hoveredDeptName) return null;

    return visibleDepartments.find((d) => d.name === hoveredDeptName) || null;
  }, [hoveredDeptName, visibleDepartments]);

  const hoveredDeptResolved = useMemo(() => {
    if (!hoveredDept) return null;

    return getDepartmentFromSelectedAddress(hoveredDept);
  }, [
    hoveredDept,
    selectedAddressValue,
    departmentsByAddress,
    departmentsSource,
    departmentsFromAllAddresses,
  ]);

  const activeDeptColor =
    hoveredDeptResolved?.themeColor ||
    hoveredDept?.themeColor ||
    visibleDepartments.find((d) => isDeptSelected(d))?.themeColor ||
    primaryColor;

  const dynamicFlyoutTitle = darkenHex(activeDeptColor, 8);
  const dynamicFlyoutText = darkenHex(activeDeptColor, 2);

  const hoveredDeptItems = useMemo(() => {
    if (!hoveredDeptResolved) return [];

    return hoveredDeptResolved.items.filter(itemVisible);
  }, [hoveredDeptResolved]);

  useEffect(() => {
    const selectedDept = visibleDepartments.find((dept) =>
      isDeptSelected(dept)
    );

    if (selectedDept) {
      setDepartmentColor(selectedDept.themeColor || "#0a343d");
    } else {
      setDepartmentColor("#0a343d");
    }
  }, [visibleDepartments, currentDeptFromPath, setDepartmentColor]);

  const getMainPath = (dept: Department, item: MainItem) => {
    const baseDeptPath = `/${getDeptRouteKey(dept)}`;
    const mainSlug = getMainSlug(item);
    const baseMainPath = `${baseDeptPath}/${mainSlug}`;

    const visibleSubs = getRenderableSubitems(item.subitems || []).filter(
      subItemVisible
    );

    if (
      MAIN_THAT_GO_TO_FIRST_SUB.has(norm(item.label)) &&
      visibleSubs.length > 0
    ) {
      return getFirstVisibleNestedPath(dept, item, visibleSubs) || baseMainPath;
    }

    return baseMainPath;
  };

  const isPathSelected = (
    dept: Department,
    item: MainItem,
    sub: SubItem,
    ancestry: SubItem[] = []
  ): boolean => {
    const currentPath = buildSubPath(dept, item, sub, ancestry);

    if (
      location.pathname === currentPath ||
      location.pathname.startsWith(`${currentPath}/`)
    ) {
      return true;
    }

    const children = getRenderableSubitems(sub.children || []).filter(
      subItemVisible
    );

    return children.some((child) =>
      isPathSelected(dept, item, child, [...ancestry, sub])
    );
  };

  const scheduleCloseFlyout = (
    event?: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    if (event && event.clientY <= HEADER_HEIGHT) {
      cancelCloseFlyout();
      return;
    }

    if (closeFlyoutTimer.current) {
      window.clearTimeout(closeFlyoutTimer.current);
    }

    closeFlyoutTimer.current = window.setTimeout(() => {
      setHoveredDeptName(null);
      setHoveredRailDeptName(null);
      setOpenActionDropdownKey(null);
    }, 120);
  };

  const cancelCloseFlyout = () => {
    if (closeFlyoutTimer.current) {
      window.clearTimeout(closeFlyoutTimer.current);
      closeFlyoutTimer.current = null;
    }
  };

  const openOverlayForDepartment = (dept: Department) => {
    if (!canSeeDept(dept)) return;

    cancelCloseFlyout();

    setHoveredDeptName(dept.name);
    setHoveredRailDeptName(dept.name);
    setOpenActionDropdownKey(null);
  };

  const closeOverlay = () => {
    cancelCloseFlyout();

    setHoveredDeptName(null);
    setHoveredRailDeptName(null);
    setOpenActionDropdownKey(null);
  };

  const handleRailDepartmentClick = (dept: Department) => {
    if (!canSeeDept(dept)) return;

    closeOverlay();

    if (isMobile) {
      setIsOpen(false);
    }

    navigate(`/${getDeptRouteKey(dept)}`);
  };

  const handleMenuFunction = (
    dept: Department,
    item: MainItem,
    action: ModuleAction,
    isMobileView = false
  ) => {
    if (isMobileView) setIsOpen(false);

    closeOverlay();

    if (!action.actionKey) {
      console.warn("Ação de menu sem actionKey:", action);
      return;
    }

    window.dispatchEvent(
      new CustomEvent("logvi:menu-action", {
        detail: {
          actionKey: action.actionKey,
          label: action.label,
          codigo: action.codigo || null,

          departamento: {
            id: dept.id ?? null,
            name: dept.name,
            slug: dept.slug || null,
            routeKey: getDeptRouteKey(dept),
          },

          modulo: {
            id: item.id ?? null,
            label: item.label,
            codigo: item.codigo || null,
            slug: getMainSlug(item),
          },

          pathContext: {
            pathname: location.pathname,
            selectedAddressValue: getSelectedAddressValue(),
          },
        },
      })
    );
  };

  const showBackdrop = false;

  const groupedFlyoutItems = useMemo<FlyoutColumn[]>(() => {
    if (!hoveredDeptItems.length) return [];

    const standbyItems = hoveredDeptItems.filter((item) =>
      norm(item.label).startsWith("standby")
    );

    const normalItems = hoveredDeptItems.filter(
      (item) => !norm(item.label).startsWith("standby")
    );

    const groups: FlyoutColumn[] = normalItems.map((item) => ({
      type: "single",
      item,
    }));

    if (standbyItems.length) {
      groups.push({
        type: "standby_group",
        items: standbyItems,
      });
    }

    return groups;
  }, [hoveredDeptItems]);

  const canRunModuleAction = (item: MainItem, action: ModuleAction) => {
    if (!item.permissions) return true;

    if (action.actionKey === "generate_report") {
      return item.permissions.canReport !== false;
    }

    return item.permissions.canView !== false;
  };

  const renderModuleActions = (
    dept: Department,
    item: MainItem,
    isMobileView = false
  ) => {
    const actions = collectModuleActions(item.subitems || []).filter((action) =>
      canRunModuleAction(item, action)
    );

    if (!actions.length) return null;

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          padding: isMobileView ? "0 0 7px 0" : "0 2px 7px 2px",
          marginBottom: 6,
          borderBottom: "1px solid rgba(15,23,42,0.06)",
          position: "relative",
          zIndex: 20,
        }}
      >
        {actions.map((action) => (
          <button
            key={`${item.codigo || item.label}-${action.actionKey}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpenAddressDropdownByDept({});
              handleMenuFunction(dept, item, action, isMobileView);
            }}
            className={`${LEFTBAR_CLASS}-module-action`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              minHeight: 25,
              padding: "5px 8px",
              border: `1px solid ${toRgba(
                dept.themeColor || activeDeptColor,
                0.2
              )}`,
              borderRadius: 6,
              background: toRgba(dept.themeColor || activeDeptColor, 0.06),
              color: darkenHex(dept.themeColor || activeDeptColor, 6),
              fontSize: 11.2,
              fontWeight: 650,
              lineHeight: 1,
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            <FileText size={12} />
            {action.label}
          </button>
        ))}
      </div>
    );
  };

  const renderNestedSubItems = (
    dept: Department,
    item: MainItem,
    subitems: SubItem[],
    level = 0,
    isMobileView = false,
    ancestry: SubItem[] = []
  ): React.ReactNode => {
    const visibleTreeItems =
      getRenderableSubitems(subitems).filter(subItemVisible);

    return visibleTreeItems.map((sub, idx) => {
      const children = getRenderableSubitems(sub.children || []).filter(
        subItemVisible
      );

      const subPath = buildSubPath(dept, item, sub, ancestry);
      const isSelected = isPathSelected(dept, item, sub, ancestry);
      const isChild = level > 0;

      const deptColor = dept.themeColor || activeDeptColor || "#0a343d";

      const commonContent = (
        <>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 10,
              minWidth: 10,
              color: isChild ? "#FFFFFF" : toRgba(deptColor, 0.86),
              opacity: isChild ? 0.9 : 1,
              flexShrink: 0,
            }}
          >
            <MiniActionIcon label={sub.label} />
          </span>

          <span
            style={{
              display: "block",
              minWidth: 0,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: isChild
                ? "#8B93A8"
                : isSelected
                ? darkenHex(deptColor, 6)
                : toRgba(deptColor, 0.92),
              fontSize: isChild ? 11.1 : 11.8,
              fontWeight: isChild ? 400 : isSelected ? 600 : 500,
              lineHeight: 1.15,
            }}
            title={sub.label}
          >
            {sub.label}
          </span>

          {isDev && sub.developed === false ? <DevBadge /> : null}
        </>
      );

      const commonStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        minHeight: isChild ? 22 : 25,
        padding: isChild ? "3px 7px 3px 7px" : "4px 7px",
        marginLeft: 0,
        marginBottom: children.length > 0 ? 1 : 2,
        borderRadius: 7,
        textDecoration: "none",
        background: isSelected
          ? toRgba(deptColor, isChild ? 0.07 : 0.11)
          : "transparent",
        transition: "background 160ms ease, color 160ms ease",
        boxSizing: "border-box",
      };

      return (
        <div
          key={`${item.label}-${
            sub.codigo || sub.label
          }-${level}-${idx}-${ancestry
            .map((a) => a.codigo || a.label)
            .join("-")}`}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: level === 0 ? 0 : 1,
          }}
        >
          <NavLink
            to={subPath}
            onClick={() => {
              if (isMobileView) setIsOpen(false);
              closeOverlay();
            }}
            className={`${LEFTBAR_CLASS}-sub-link`}
            style={commonStyle}
          >
            {commonContent}
          </NavLink>

          {children.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                marginLeft: isMobileView ? 10 : 12,
                paddingLeft: 7,
                borderLeft: "1px solid rgba(120,130,150,0.18)",
              }}
            >
              {renderNestedSubItems(
                dept,
                item,
                children,
                level + 1,
                isMobileView,
                [...ancestry, sub]
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const renderSubTreeDesktop = (dept: Department, item: MainItem) => {
    const visibleSubs = getRenderableSubitems(item.subitems || []).filter(
      subItemVisible
    );

    if (!visibleSubs.length) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          paddingLeft: 3,
          paddingRight: 2,
          paddingBottom: 2,
        }}
      >
        {renderNestedSubItems(dept, item, visibleSubs, 0, false, [])}
      </div>
    );
  };

  const renderSubTreeMobile = (dept: Department, item: MainItem) => {
    const visibleSubs = getRenderableSubitems(item.subitems || []).filter(
      subItemVisible
    );

    if (!visibleSubs.length) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          marginBottom: 4,
        }}
      >
        {renderNestedSubItems(dept, item, visibleSubs, 0, true, [])}
      </div>
    );
  };

  const renderEmptyState = (dept: Department) => (
    <div
      style={{
        width: "100%",
        border: "1px dashed rgba(15,23,42,0.10)",
        borderRadius: 10,
        padding: "16px 14px",
        color: "#7C8697",
        fontSize: 13,
        lineHeight: 1.45,
        background: "rgba(255,255,255,0.82)",
        boxSizing: "border-box",
      }}
    >
      Nenhum módulo vinculado para <strong>{dept.name}</strong> neste endereço.
    </div>
  );

  const renderDesktopModuleCard = (
    resolvedDept: Department,
    item: MainItem,
    groupKey: string
  ) => {
    const baseDeptPath = `/${getDeptRouteKey(resolvedDept)}`;
    const baseMainPath = `${baseDeptPath}/${getMainSlug(item)}`;

    const isMainSelected = location.pathname.startsWith(baseMainPath);

    const visibleSubtree = getRenderableSubitems(item.subitems || []);
    const hasVisibleSubtree = visibleSubtree.filter(subItemVisible).length > 0;
    const hasActions = collectModuleActions(item.subitems || []).length > 0;

    return (
      <div
        key={groupKey}
        className={`${LEFTBAR_CLASS}-col`}
        style={{
          borderColor: "rgba(15,23,42,0.08)",
          background: "#FFFFFF",
        }}
      >
        <div
          className={`${LEFTBAR_CLASS}-col-inner`}
          style={{
            maxHeight: `calc(100vh - ${HEADER_HEIGHT}px - 118px - ${FLYOUT_BOTTOM_GAP}px)`,
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 2,
          }}
        >
          <NavLink
            to={getMainPath(resolvedDept, item)}
            className={`${LEFTBAR_CLASS}-main-link`}
            onClick={closeOverlay}
            style={{
              color: dynamicFlyoutText,
              fontSize: 12.4,
              fontWeight: 650,
              padding: "4px 4px 7px 4px",
              borderBottom:
                hasVisibleSubtree || hasActions
                  ? "1px solid rgba(15,23,42,0.06)"
                  : "none",
              marginBottom: hasVisibleSubtree || hasActions ? 5 : 0,
              justifyContent: "space-between",
              background: "transparent",
            }}
          >
            <span
              style={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {renderLabelWithBadge(
                item.label,
                item.developed,
                item.migrated,
                item.markOrange,
                item.markBlue
              )}
            </span>
          </NavLink>

          {renderModuleActions(resolvedDept, item, false)}

          {renderSubTreeDesktop(resolvedDept, item)}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .${LEFTBAR_CLASS} {
          position: relative;
          overflow: visible;
          -webkit-font-smoothing: antialiased;
          text-rendering: geometricPrecision;
        }

        .${LEFTBAR_CLASS}-scroll {
          height: 100%;
          width: 100%;
          overflow-y: auto;
          overflow-x: visible;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.16) transparent;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar {
          width: 5px;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(174, 151, 151, 0.16);
          border-radius: 999px;
          border: 1px solid transparent;
          background-clip: padding-box;
        }

        .${LEFTBAR_CLASS}-scroll::-webkit-scrollbar-thumb:hover {
          background-color: rgba(255,255,255,0.26);
        }

        .${LEFTBAR_CLASS}-rail-button {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 46px;
          border-radius: 0;
          border: none;
          background: transparent;
          color: ${iconMuted};
          cursor: pointer;
          transition:
            background-color 180ms ease,
            color 180ms ease;
          text-decoration: none;
          overflow: hidden;
          padding: 0;
        }

        .${LEFTBAR_CLASS}-rail-button:hover {
          color: ${iconIdle};
        }

        .${LEFTBAR_CLASS}-rail-button.is-active {
          color: ${iconActive};
        }

        .${LEFTBAR_CLASS}-main-link,
        .${LEFTBAR_CLASS}-sub-link,
        .${LEFTBAR_CLASS}-list-button {
          text-decoration: none;
          color: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          gap: 7px;
          min-width: 0;
          box-sizing: border-box;
        }

        .${LEFTBAR_CLASS}-module-action {
          appearance: none;
          -webkit-appearance: none;
        }

        .${LEFTBAR_CLASS}-module-action:hover {
          background: rgba(15,23,42,0.045) !important;
        }

        .${LEFTBAR_CLASS}-flyout-shell {
          position: fixed;
          top: ${FLYOUT_TOP}px;
          left: ${FLYOUT_LEFT}px;
          right: 0;
          bottom: auto;
          height: auto;
          max-height: calc(100vh - ${HEADER_HEIGHT}px - ${FLYOUT_BOTTOM_GAP}px);
          background: ${flyoutBg};
          border-top: 1px solid ${flyoutBorder};
          border-bottom: 1px solid ${flyoutBorder};
          border-right: 1px solid ${flyoutBorder};
          border-bottom-right-radius: 16px;
          z-index: 1210;
          box-sizing: border-box;
          overflow: visible;
          display: flex;
          flex-direction: column;
          box-shadow: 0 18px 36px rgba(15,23,42,0.10);
        }

        .${LEFTBAR_CLASS}-flyout-header {
          flex: 0 0 auto;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 18px;
          min-height: 42px;
          padding: 10px ${FLYOUT_PADDING_X}px 9px ${FLYOUT_PADDING_X}px;
          border-bottom: 1px solid rgba(15,23,42,0.06);
          box-sizing: border-box;
          background: #FFFFFF;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .${LEFTBAR_CLASS}-flyout-scroll {
          flex: 0 1 auto;
          min-height: 0;
          max-height: calc(100vh - ${HEADER_HEIGHT}px - 62px - ${FLYOUT_BOTTOM_GAP}px);
          overflow-y: auto;
          overflow-x: hidden;
          padding: ${FLYOUT_PADDING_Y}px ${FLYOUT_PADDING_X}px 14px ${FLYOUT_PADDING_X}px;
          box-sizing: border-box;
          background: #FFFFFF;
          border-bottom-right-radius: 16px;
          scrollbar-width: thin;
          scrollbar-color: rgba(15,23,42,0.20) transparent;
        }

        .${LEFTBAR_CLASS}-flyout-scroll::-webkit-scrollbar,
        .${LEFTBAR_CLASS}-col-inner::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        .${LEFTBAR_CLASS}-flyout-scroll::-webkit-scrollbar-track,
        .${LEFTBAR_CLASS}-col-inner::-webkit-scrollbar-track {
          background: transparent;
        }

        .${LEFTBAR_CLASS}-flyout-scroll::-webkit-scrollbar-thumb,
        .${LEFTBAR_CLASS}-col-inner::-webkit-scrollbar-thumb {
          background: rgba(15,23,42,0.18);
          border-radius: 999px;
        }

        .${LEFTBAR_CLASS}-flyout-scroll::-webkit-scrollbar-thumb:hover,
        .${LEFTBAR_CLASS}-col-inner::-webkit-scrollbar-thumb:hover {
          background: rgba(15,23,42,0.28);
        }

        .${LEFTBAR_CLASS}-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(${COLUMN_MIN_WIDTH}px, ${COLUMN_WIDTH}px));
          align-items: start;
          align-content: start;
          justify-content: start;
          gap: ${COLUMN_GAP}px;
          width: 100%;
          box-sizing: border-box;
        }

        .${LEFTBAR_CLASS}-col {
          width: 100%;
          min-width: 0;
          max-width: ${COLUMN_WIDTH}px;
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 11px;
          background: #FFFFFF;
          padding: 7px 7px 6px;
          box-sizing: border-box;
          box-shadow: 0 6px 16px rgba(15,23,42,0.045);
          overflow: hidden;
        }

        .${LEFTBAR_CLASS}-col-inner {
          scrollbar-width: thin;
          scrollbar-color: rgba(15,23,42,0.18) transparent;
        }

        .${LEFTBAR_CLASS}-sub-link:hover {
          background: rgba(15,23,42,0.045) !important;
        }

        .${LEFTBAR_CLASS}-main-link:hover {
          background: rgba(15,23,42,0.035);
        }

        @media (max-width: 1280px) {
          .${LEFTBAR_CLASS}-grid {
            grid-template-columns: repeat(auto-fill, minmax(224px, 242px));
          }

          .${LEFTBAR_CLASS}-col {
            max-width: 242px;
          }
        }

        @media (max-width: 1080px) {
          .${LEFTBAR_CLASS}-grid {
            grid-template-columns: repeat(auto-fill, minmax(216px, 236px));
          }

          .${LEFTBAR_CLASS}-col {
            max-width: 236px;
          }
        }
      `}</style>

      {isMobile && (
        <div
          style={{
            ...styles.mobileHeader,
            background: `linear-gradient(270deg, ${primaryColor} 37.72%, ${darkenHex(
              primaryColor,
              16
            )} 82.21%)`,
          }}
        >
          <img
            src={MenuIcon}
            alt="Menu"
            style={{ width: "27px", cursor: "pointer" }}
            onClick={() => setIsOpen(true)}
          />

          <p style={styles.titleMobile}>{systemTitle}</p>
        </div>
      )}

      {isMobile && isOpen && (
        <motion.div
          style={{
            position: "fixed",
            top: HEADER_HEIGHT,
            left: RAIL_WIDTH,
            right: 0,
            background: flyoutBg,
            borderTop: `1px solid ${flyoutBorder}`,
            borderBottom: `1px solid ${flyoutBorder}`,
            borderRight: `1px solid ${flyoutBorder}`,
            padding: `${FLYOUT_PADDING_Y}px ${FLYOUT_PADDING_X}px`,
            zIndex: 1210,
            boxSizing: "border-box",
            overflowX: "auto",
            overflowY: "visible",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {!isMobile && showBackdrop && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}

      {(isOpen || !isMobile) && (
        <AnimatePresence initial={false}>
          <motion.aside
            key="sidebar"
            className={LEFTBAR_CLASS}
            style={{
              ...styles.container,
              background:
                "linear-gradient(180deg, #0D3C46 0%, #142F35 100px, #083640 100%)",
              transform: isMobile
                ? isOpen
                  ? "translateX(0)"
                  : "translateX(-100%)"
                : "translateX(0)",
              zIndex: 1200,
            }}
            initial={isMobile ? "hidden" : false}
            animate="show"
            exit="hidden"
            variants={sidebarVariants}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              if (!isMobile) cancelCloseFlyout();
            }}
            onMouseLeave={(e) => {
              if (!isMobile) scheduleCloseFlyout(e);
            }}
          >
            {isMobile && (
              <div style={{ textAlign: "right", padding: "10px 10px 0 10px" }}>
                <motion.button
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✕
                </motion.button>
              </div>
            )}

            <div
              className={`${LEFTBAR_CLASS}-scroll`}
              style={styles.scrollArea}
            >
              <div style={styles.inner}>
                {visibleDepartments.map((dept) => {
                  const deptSelected = isDeptSelected(dept);
                  const active = hoveredDeptName === dept.name || deptSelected;
                  const deptThemeColor = dept.themeColor || "#0a343d";

                  const hoverBackground = toRgba(deptThemeColor, 0.14);
                  const activeBackground = deptThemeColor;

                  return (
                    <div key={dept.name}>
                      <button
                        type="button"
                        className={`${LEFTBAR_CLASS}-rail-button ${
                          active ? "is-active" : ""
                        }`}
                        title={dept.name}
                        onMouseEnter={() => {
                          if (!isMobile) {
                            openOverlayForDepartment(dept);
                          }
                        }}
                        onClick={() => {
                          handleRailDepartmentClick(dept);
                        }}
                        style={{
                          background: deptSelected
                            ? activeBackground
                            : hoveredRailDeptName === dept.name
                            ? hoverBackground
                            : "transparent",
                        }}
                      >
                        <DeptInternetIcon
                          iconKey={dept.iconKey}
                          size={21}
                          color={active ? "#ffffff" : "rgba(255,255,255,0.82)"}
                          alt={dept.name}
                        />
                      </button>

                      {isMobile && (
                        <AnimatePresence initial={false}>
                          {openDepts.includes(dept.name) && (
                            <motion.div
                              initial="collapsed"
                              animate="open"
                              exit="collapsed"
                              variants={mobileDeptVariants}
                              style={{
                                overflow: "hidden",
                                width: 260,
                                marginLeft: 8,
                                marginTop: 0,
                                background: "#FFFFFF",
                                border: "1px solid rgba(15,23,42,0.08)",
                                borderRadius: 14,
                                padding: "10px",
                              }}
                            >
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  gap: 12,
                                  padding: "4px 8px 10px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color:
                                      dept.themeColor || dynamicFlyoutTitle,
                                    minWidth: 0,
                                  }}
                                >
                                  {dept.name}
                                </div>

                                {renderAddressSelector(dept)}
                              </div>

                              <AnimatePresence mode="wait" initial={false}>
                                <motion.div
                                  key={`${
                                    dept.name
                                  }-${getSelectedAddressValue()}`}
                                  variants={contentSwapVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                >
                                  {getDepartmentFromSelectedAddress(
                                    dept
                                  ).items.filter(itemVisible).length === 0
                                    ? renderEmptyState(dept)
                                    : getDepartmentFromSelectedAddress(dept)
                                        .items.filter(itemVisible)
                                        .map((item) => {
                                          const resolvedDept =
                                            getDepartmentFromSelectedAddress(
                                              dept
                                            );

                                          const mainPath = getMainPath(
                                            resolvedDept,
                                            item
                                          );

                                          const baseDeptPath = `/${getDeptRouteKey(
                                            resolvedDept
                                          )}`;

                                          const baseMainPath = `${baseDeptPath}/${getMainSlug(
                                            item
                                          )}`;

                                          const isMainSelected =
                                            location.pathname.startsWith(
                                              baseMainPath
                                            );

                                          return (
                                            <div key={item.label}>
                                              <NavLink
                                                to={mainPath}
                                                onClick={() => {
                                                  setIsOpen(false);
                                                  closeOverlay();
                                                }}
                                                className={`${LEFTBAR_CLASS}-main-link`}
                                                style={{
                                                  borderRadius: 10,
                                                  padding: "9px 10px",
                                                  marginBottom: 4,
                                                  background: isMainSelected
                                                    ? toRgba(primaryColor, 0.08)
                                                    : "transparent",
                                                  color: dynamicFlyoutText,
                                                }}
                                              >
                                                <OptionIcon
                                                  label={
                                                    norm(item.label) ===
                                                    norm("Laboratório - Cases")
                                                      ? "circle-outline"
                                                      : item.label
                                                  }
                                                  size={17}
                                                />

                                                {renderLabelWithBadge(
                                                  item.label,
                                                  item.developed,
                                                  item.migrated,
                                                  item.markOrange,
                                                  item.markBlue
                                                )}
                                              </NavLink>

                                              {renderModuleActions(
                                                resolvedDept,
                                                item,
                                                true
                                              )}

                                              {renderSubTreeMobile(
                                                resolvedDept,
                                                item
                                              )}
                                            </div>
                                          );
                                        })}
                                </motion.div>
                              </AnimatePresence>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {!isMobile && (
              <AnimatePresence initial={false}>
                {hoveredDept && (
                  <motion.div
                    key={hoveredDept.name}
                    variants={flyoutVariants}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    onMouseEnter={cancelCloseFlyout}
                    onMouseLeave={(e) => scheduleCloseFlyout(e)}
                    className={`${LEFTBAR_CLASS}-flyout-shell`}
                  >
                    <div
                      className={`${LEFTBAR_CLASS}-flyout-header`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        style={{
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <div
                          style={{
                            color: dynamicFlyoutTitle,
                            fontSize: 13.8,
                            fontWeight: 700,
                            lineHeight: 1.2,
                            minWidth: 0,
                            letterSpacing: 0.1,
                          }}
                        >
                          {hoveredDept.name}
                        </div>

                        <div
                          style={{
                            color: "#8A929D",
                            fontSize: 11.2,
                            fontWeight: 500,
                            lineHeight: 1.25,
                          }}
                        >
                          Selecione um módulo ou ação disponível
                        </div>
                      </div>

                      <div
                        style={{
                          flexShrink: 0,
                          paddingTop: 1,
                        }}
                      >
                        {renderAddressSelector(hoveredDept)}
                      </div>
                    </div>

                    <div className={`${LEFTBAR_CLASS}-flyout-scroll`}>
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                          key={`${
                            hoveredDept.name
                          }-${getSelectedAddressValue()}`}
                          variants={contentSwapVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                        >
                          {groupedFlyoutItems.length === 0 ? (
                            renderEmptyState(hoveredDept)
                          ) : (
                            <div className={`${LEFTBAR_CLASS}-grid`}>
                              {groupedFlyoutItems.map((group, groupIndex) => {
                                const resolvedDept =
                                  hoveredDeptResolved || hoveredDept;

                                if (group.type === "single") {
                                  return renderDesktopModuleCard(
                                    resolvedDept,
                                    group.item,
                                    `single-${group.item.label}-${groupIndex}`
                                  );
                                }

                                return (
                                  <div
                                    key={`standby-group-${groupIndex}`}
                                    className={`${LEFTBAR_CLASS}-col`}
                                    style={{
                                      background: "#FFFFFF",
                                      borderColor: "rgba(15,23,42,0.08)",
                                    }}
                                  >
                                    <div
                                      className={`${LEFTBAR_CLASS}-col-inner`}
                                      style={{
                                        maxHeight: `calc(100vh - ${HEADER_HEIGHT}px - 118px - ${FLYOUT_BOTTOM_GAP}px)`,
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        paddingRight: 2,
                                      }}
                                    >
                                      <div
                                        style={{
                                          color: dynamicFlyoutText,
                                          fontSize: 12.4,
                                          fontWeight: 650,
                                          padding: "4px 4px 7px 4px",
                                          borderBottom:
                                            "1px solid rgba(15,23,42,0.06)",
                                          marginBottom: 5,
                                        }}
                                      >
                                        Standby
                                      </div>

                                      {group.items.map((item) => (
                                        <div
                                          key={item.label}
                                          style={{ marginBottom: 12 }}
                                        >
                                          <NavLink
                                            to={getMainPath(resolvedDept, item)}
                                            className={`${LEFTBAR_CLASS}-main-link`}
                                            onClick={closeOverlay}
                                            style={{
                                              color: dynamicFlyoutText,
                                              fontSize: 12.2,
                                              fontWeight: 600,
                                              padding: "2px 2px 6px 2px",
                                            }}
                                          >
                                            {renderLabelWithBadge(
                                              item.label,
                                              item.developed,
                                              item.migrated,
                                              item.markOrange,
                                              item.markBlue
                                            )}
                                          </NavLink>

                                          {renderModuleActions(
                                            resolvedDept,
                                            item,
                                            false
                                          )}

                                          {renderSubTreeDesktop(
                                            resolvedDept,
                                            item
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </motion.aside>
        </AnimatePresence>
      )}
    </>
  );
};

export default LeftBar;
